import { superOnaContentTabAtom } from "@/atoms/ui-state";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import { Agents, useCreateAgentExecution, useSendToAgentExecution } from "@/queries/agent-queries";
import { formatError } from "@/utils/errors";
import {
    AgentCodeContext,
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_Input,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSetAtom } from "jotai";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * Shared hook for sending messages to the in-environment Ona agent.
 * Handles agent execution creation, message sending, busy state, and
 * switching to the conversation tab on success.
 */
export function useAgentAction(environmentId: string) {
    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const agentExecution = getAgentExecution(environmentConversation);

    const createAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();

    const { toast } = useToast();
    const setSuperOnaContentTab = useSetAtom(superOnaContentTabAtom);

    const isAgentBusy =
        agentExecution?.status?.phase === AgentExecution_Phase.RUNNING ||
        agentExecution?.status?.phase === AgentExecution_Phase.PENDING;

    const isPending = createAgentExecution.isPending || sendToAgentExecution.isPending;

    const getOrCreateExecution = useCallback(async () => {
        if (agentExecution?.id) return agentExecution.id;
        const { agentExecutionId } = await createAgentExecution.mutateAsync({
            agentId: Agents.InEnvironmentOna.id,
            codeContext: new AgentCodeContext({
                context: { case: "environmentId", value: environmentId },
            }),
        });
        return agentExecutionId;
    }, [agentExecution?.id, createAgentExecution, environmentId]);

    const sendMessage = useCallback(
        async (executionId: string, content: string) => {
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: executionId,
                    input: {
                        case: "userInput" as const,
                        value: new UserInputBlock({
                            id: uuidv4(),
                            inputs: [
                                new UserInputBlock_Input({
                                    input: {
                                        case: "text",
                                        value: new UserInputBlock_TextInput({ content }),
                                    },
                                }),
                            ],
                        }),
                    },
                }),
            );
        },
        [sendToAgentExecution],
    );

    /** Send a message to the agent, switching to the conversation on success. */
    const sendAgentMessage = useCallback(
        async (content: string) => {
            const executionId = await getOrCreateExecution();
            await sendMessage(executionId, content);
            setSuperOnaContentTab((prev) => ({ tab: "conversation", seq: prev.seq + 1 }));
        },
        [getOrCreateExecution, sendMessage, setSuperOnaContentTab],
    );

    const showError = useCallback(
        (title: string, error: unknown) => {
            toast({ title, description: formatError(error) });
        },
        [toast],
    );

    return { isAgentBusy, isPending, sendAgentMessage, showError };
}
